/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { getRouteInitData, State } from '@gh/common-shared';
import { FocusedCell } from '@gh/core-ui-grid';
import { Observable } from 'rxjs';
import { SelectionModel, emptySelectionModel } from '@gh/core-data';
import { DialogService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, map$, switchMap$, take$ } from '@gh/rx/operators';
import { ComponentItem } from '../../resources/component-and-item-details.model';
import { ComponentDetailsFormState } from '../component-details-tab-container/component-details-form.service';
import { ComponentDetailsCommandService } from './component-details-command.service';
import { ComponentDetailsItemsGridDefinition } from './component-details-items-grid-definition.service';
import { ComponentDetailsItemsState } from './component-details-items.state';

@Component({
  standalone: false,
  selector: 'gh-component-details-items-tab',
  templateUrl: 'component-details-items-tab.component.html',
  styleUrls: ['component-details-items-tab.component.scss'],
  providers: [ComponentDetailsItemsGridDefinition],
})
export class ComponentDetailsItemsTabComponent implements OnInit {

  sourceSelectionModel = emptySelectionModel();

  constructor(private route: ActivatedRoute,
              public gridDefinition: ComponentDetailsItemsGridDefinition,
              private itemsState: State<ComponentDetailsItemsState>,
              private commands: ComponentDetailsCommandService,
              private form: ComponentDetailsFormState,
              private dialogService: DialogService) {

  }

  get editable(): boolean {
    return this.form.editable && !this.form.locked;
  }

  get items(): Observable<ComponentItem[]> {
    return this.itemsState.select((state) => state.items);
  }

  get total(): number {
    return this.form.cost;
  }

  get focusedCell(): Observable<Maybe<FocusedCell>> {
    return this.itemsState.select((state) => state.focusedCell);
  }

  ngOnInit(): void {
    const { itemsGridLayout } = getRouteInitData(this.route);

    this.gridDefinition.bind(itemsGridLayout);
  }

  onAdd(): void {
    this.commands.addItem();
  }

  onRemove(item: ComponentItem): void {
    this.commands.removeItem(item.uniqueId);
  }

  onItemSelectionChanged(model: SelectionModel): void {
    this.sourceSelectionModel = model;
  }

  getSelectedItems(): Observable<ComponentItem[]> {
    return this.items.pipe(
      map$((items) => {
        if (!items || items.length === 0) {
          return [];
        }

        if (this.sourceSelectionModel.isExcludeMode) {
          return items.filter((item) => !this.sourceSelectionModel.idList.includes(item.uniqueId));
        } else {
          return items.filter((item) => this.sourceSelectionModel.idList.includes(item.uniqueId));
        }
      }));
  }

  hasSelectedItems(): Observable<boolean> {
    return this.getSelectedItems().pipe(
      map$((selectedItems) => selectedItems.length > 0));
  }

  onDeleteSelected(): void {
    this.getSelectedItems().pipe(
      take$(1),
      filter$((selectedItems) => selectedItems.length > 0),
      switchMap$((selectedItems) => {
        const count = selectedItems.length;
        const confirmConfig = count === 1
          ? yesNoConfirmation('msg_bulk_remove_single_item', { code: selectedItems[0].code })
          : yesNoConfirmation('msg_bulk_remove_n_items', { count: String(count) });
        return this.dialogService.confirmation(confirmConfig).pipe(
          filter$(yesAnswer),
          map$(() => selectedItems));
      }),
    ).subscribe((selectedItems) => {
      this.commands.removeItems(selectedItems.map((item) => item.uniqueId));
      this.sourceSelectionModel = emptySelectionModel();
      this.onItemSelectionChanged(this.sourceSelectionModel);
    });
  }
}
